import { Suspense, lazy, useEffect } from "react"
import * as Sentry from "@sentry/react"

import { Providers } from "./providers"
import { AppRouter } from "./router"

import { PvpRoomRoot } from "@/features/pvp-room"
import { WheelEngineProvider } from "@/features/pvp-roulette-wheel/engine/WheelEngineContext"
import SegmentSheet from "@/features/segment-sheet/ui/SegmentSheet"
import { TournamentWelcomeModal } from "@/features/tournament"

import { AppLoader } from "@/components/AppLoader"
import { Toaster } from "@/components/ui/sonner"
import { ConfigError } from "@/components/ConfigError"

import { apiClient } from "@/shared/api"
import { useNetworkStore } from "@/shared/store"
import { useFeatureFlag } from "@/shared/features"

const LazyDemoModeRunner = import.meta.env.DEV
  ? lazy(() =>
      import("@/shared/dev/DemoModeRunner").then((module) => ({
        default: module.DemoModeRunner,
      }))
    )
  : null

const AppContent = () => {
  const setIsOnline = useNetworkStore((s) => s.setIsOnline)
  const tournamentsEnabled = useFeatureFlag("tournaments")

  useEffect(() => {
    apiClient.ensureSession().catch(() => {})
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return

    setIsOnline(navigator.onLine)
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [setIsOnline])

  return (
    <AppLoader>
      <PvpRoomRoot />
      {LazyDemoModeRunner && (
        <Suspense fallback={null}>
          <LazyDemoModeRunner />
        </Suspense>
      )}
      <WheelEngineProvider>
        <AppRouter />
      </WheelEngineProvider>

      <SegmentSheet />
      {tournamentsEnabled && <TournamentWelcomeModal />}
    </AppLoader>
  )
}

const App = () => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <ConfigError
          error={error instanceof Error ? error : new Error(String(error))}
        />
      )}
    >
      <Providers>
        <AppContent />
        <Toaster
          position="top-center"
          offset={{ top: "var(--toast-offset-top)" }}
          mobileOffset={{ top: "var(--toast-offset-top)" }}
          duration={3000}
        />
      </Providers>
    </Sentry.ErrorBoundary>
  )
}

export default App
